import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ISubjectMobile } from "./ISubjectMobile";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addSubjectMobile } from "../../Redux/Slices/SubjectMobile/AddSubjectMobileSlice";
import { EditSubjectMobile } from "../../Redux/Slices/SubjectMobile/EditSubjectMobileSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import MultiSelect from "../../Components/MultiSelect/MultiSelect";
import { TextField } from "@mui/material";

interface SubjectMobileFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: ISubjectMobile;
  setInitialValues: React.Dispatch<React.SetStateAction<ISubjectMobile>>;
}

const SubjectMobileForm: FunctionComponent<SubjectMobileFormProps> = (
  props
) => {
  const dispatch = useAppDispatch();

  // Define allowed image MIME types
  const allowedMimeTypes = ["image/jpeg", "image/png", "image/gif"];

  // Define the maximum file size (e.g., 5MB)
  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 5 MB in bytes

  const userSchema = z.object({
    id: z.number().optional(),
    name: z
      .string({ message: "Please enter subject name" })
      .min(1, { message: "Please enter subject name" }),
    sequence: z.number({ message: "Please enter sequence" }),
    status: z.boolean({ message: "Please select the status" }),
    year_levels: z
      .array(z.number({ message: "Please select year level" }))
      .min(1, { message: "Please select year level" }),
    image_url: z.union([
      z
        .instanceof(File)
        .refine((file) => !!file, {
          message: "File is required.",
        })
        .refine((file) => allowedMimeTypes.includes(file.type), {
          message:
            "Invalid file type. Only JPEG, PNG, and GIF files are allowed.",
        })
        .refine((file) => file.size <= MAX_FILE_SIZE, {
          message: "File size should not exceed 1MB.",
        }),

      z.string().url(),
    ]),
  });
  const methods = useForm<ISubjectMobile>({
    defaultValues: {
      ...props.initialValues,
      image_url:
        props?.initialValues.image_url != null
          ? props?.initialValues.image_url
          : undefined,
      status: props.initialValues.id ? props.initialValues.status : true,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<ISubjectMobile> = (val) => {
    console.log(val);

    if (val.id) {
      dispatch(EditSubjectMobile(val));
    } else {
      dispatch(addSubjectMobile(val));
    }
  };

  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, [dispatch, props]);

  // Status list
  const SubjectMobileSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];
  const { data: listyear } = useAppSelector(
    (state) => state.ListyearGroupSlice
  );

  const yearlevelList = Array.isArray(listyear?.data)
    ? listyear?.data
        ?.filter((role) => role.status !== false) // Filter out roles where status is false
        .map((listyear) => ({
          label: listyear.year_group!,
          value: listyear.id,
        }))
    : [];
  const imageFile: any = methods.watch("image_url");

  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        <TextInputFieldC name="name" label="Name" type="text" />
        <NumberInputField name="sequence" label="Sequence" type="number" />
        <MultiSelect
          name="year_levels"
          label="Year Levels"
          options={yearlevelList ?? []}
          value={
            yearlevelList?.filter((list: any) =>
              methods.getValues("year_levels")?.includes(list?.value)
            ) ?? []
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption[];
            methods.setValue(
              "year_levels",
              val.map((val) => val.value) ?? null,
              {
                shouldValidate: true,
              }
            );
          }}
        />
        <TextField
          fullWidth
          type="file"
          // size="small"
          label="Question Image"
          InputLabelProps={{ shrink: true }} // Correct way to shrink label
          onChange={(e: any) => {
            const file = e.target.files ? e.target.files[0] : null;

            methods.setValue("image_url", file, {
              shouldValidate: true,
            });
          }}
          error={!!methods.formState.errors.image_url}
          helperText={
            !!methods.formState.errors.image_url
              ? String(
                  methods.formState.errors.image_url.message ===
                    "Input not instance of File"
                    ? "Please upload  image"
                    : methods.formState.errors.image_url.message
                )
              : ""
          }
        />
        <Select
          name="status"
          label="Status"
          options={SubjectMobileSatus}
          value={
            SubjectMobileSatus?.find(
              (role) => role.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, { shouldValidate: true });
          }}
        />
        {imageFile && (
          <div>
            <span style={{ color: "#009DFF" }}>Preview for Image</span>
            <img
              src={
                typeof imageFile === "string"
                  ? imageFile
                  : URL.createObjectURL(imageFile)
              }
              alt="Uploaded preview"
              style={{ maxWidth: "100%", height: "auto" }}
            />
            {/* <img
              src={imageFile}
              alt="Uploaded preview"
              style={{ maxWidth: "100%", height: "auto" }}
            /> */}
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default memo(SubjectMobileForm);
